import React, { useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { LoginScreen } from './components/LoginScreen';
import { ResetPassword } from './components/ResetPassword';
import { ToastProvider } from './components/Toast';
import { supabase } from './supabaseClient';
import { useBoardStore } from './store/boardStore';
import { LoadingOverlay } from './components/LoadingComponents';

const TimelineBoard = lazy(() => import('./components/TimelineBoard').then(m => ({ default: m.TimelineBoard })));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const UserAssetsDashboard = lazy(() => import('./components/UserAssetsDashboard').then(m => ({ default: m.UserAssetsDashboard })));
const ClientAssets = lazy(() => import('./components/ClientAssets').then(m => ({ default: m.ClientAssets })));
const DemandsView = lazy(() => import('./components/DemandsView').then(m => ({ default: m.DemandsView })));
const PlanningView = lazy(() => import('./components/PlanningView').then(m => ({ default: m.PlanningView })));
const ClientApprovalView = lazy(() => import('./components/ClientApprovalView').then(m => ({ default: m.ClientApprovalView })));

import './global-styles.css';
import './modern-components.css';


// Wrapper to force remount on clientId change
function TimelineWrapper({ user, initialView }) {
  const { clientId } = useParams();
  return <TimelineBoard key={`${clientId}-${initialView}`} user={user} initialView={initialView} />;
}

function App() {
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('timeline-session');
    return saved ? JSON.parse(saved) : null;
  });

  // ... inside App component ...


  const lastFetchedUserId = React.useRef(null);

  React.useEffect(() => {
    const handleSession = async (sessionUser) => {
      if (!sessionUser) {
        if (currentUser) {
          console.warn("Cleared stale session from localStorage");
        }
        setCurrentUser(null);
        localStorage.removeItem('timeline-session');
        lastFetchedUserId.current = null;
        return;
      }

      if (lastFetchedUserId.current === sessionUser.id) {
        return; // Already fetched for this user, avoiding double fetch
      }
      lastFetchedUserId.current = sessionUser.id;

      const { data: profile } = await supabase
        .from('profiles')
        .select('*, profile_timelines(timeline_id)')
        .eq('auth_user_id', sessionUser.id)
        .single();

      const fullUser = profile ? { ...sessionUser, ...profile } : sessionUser;
      setCurrentUser(fullUser);
      localStorage.setItem('timeline-session', JSON.stringify(fullUser));
    };

    const initAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      handleSession(session?.user);
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        handleSession(null);
        useBoardStore.getState().clearCache();
      } else if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        handleSession(session?.user);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleLogin = (user) => {
    setCurrentUser(user);
    localStorage.setItem('timeline-session', JSON.stringify(user));
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('timeline-session');
    useBoardStore.getState().clearCache();
  };

  const handleUpdateUser = (updatedUser) => {
    setCurrentUser(updatedUser);
    localStorage.setItem('timeline-session', JSON.stringify(updatedUser));
  };

  // Protected Route Wrapper
  const ProtectedRoute = ({ children }) => {
    if (!currentUser) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  return (
    <ToastProvider>
      <Router>
        <Suspense fallback={<LoadingOverlay message="Carregando aplicativo..." />}>
          <Routes>
            <Route
              path="/"
              element={
                currentUser ? <Navigate to={"/dashboard" + window.location.search} replace /> : <LoginScreen onLogin={handleLogin} />
              }
            />



            <Route
              path="/approve"
              element={<ClientApprovalView />}
            />

            <Route
              path="/reset-password"
              element={<ResetPassword />}
            />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  {currentUser?.role === 'admin' ? (
                    <AdminDashboard user={currentUser} onLogout={handleLogout} onUpdateUser={handleUpdateUser} />
                  ) : (
                    <UserAssetsDashboard user={currentUser} onLogout={handleLogout} onUpdateUser={handleUpdateUser} />
                  )}
                </ProtectedRoute>
              }
            />

            <Route
              path="/timeline/:clientId"
              element={
                <ProtectedRoute>
                  <TimelineWrapper user={currentUser} initialView="timeline" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/timeline/:clientId/workflow"
              element={
                <ProtectedRoute>
                  <TimelineWrapper user={currentUser} initialView="workflow" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/demands"
              element={
                <ProtectedRoute>
                  <DemandsView user={currentUser} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/timeline/:clientId/assets"
              element={
                <ProtectedRoute>
                  <ClientAssets user={currentUser} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/timeline/:timelineId/planning"
              element={
                <ProtectedRoute>
                  <PlanningView user={currentUser} />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </ToastProvider>
  );
}

export default App;
